<template>
    <div class="component-warranty-activation">
        <ErrorGeneral
            v-if="errorsWarantyActivated"
            :errors="errorsQuery"
        />

        <div
            v-else-if="alreadyActivated"
            class="warranty-activated-message"
        >
            <p>Warranty already activated. Thank you!</p>
        </div>

        <div
            v-else-if="success"
            class="warranty-activated-message"
        >
            <p>Thank you for completing the warranty activation!</p>
        </div>

        <form
            v-else
            class="form"
            :disabled="fetchingMutation"
        >
            <Radio
                id="wearer"
                v-model="wearer"
                :options="wearerOptions"
                class="radio"
            >
                <p class="radio-legend">
                    Is the person who purchased the ring the same person who
                    wears the ring?
                </p>
            </Radio>

            <div
                v-if="wearer === 'No'"
                class="fields"
            >
                <TextField
                    id="name"
                    v-model="name"
                    :error="namesError?.message"
                    label="Ring wearer’s name"
                    required
                    type="text"
                />

                <TextField
                    id="email"
                    v-model="email"
                    :error="emailError?.message"
                    label="Ring wearer’s email"
                    required
                    type="email"
                />

                <ThCheckbox
                    v-model="emailConsent"
                    label="Opt in to join our mailing list for exclusive access to tips and advice, news, promotions and more."
                    form-id="emailConsent"
                />
            </div>

            <FormActionErrors :errors="errorsMutation" />

            <ThButton
                :loading="fetchingMutation"
                :disabled="isSubmitDisabled"
                button-element-type="button"
                @click="submitWarranty"
            >
                Activate Warranty
            </ThButton>
        </form>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import { useRouteParams } from '@vueuse/router'

import type { BackendError } from '@/helpers/errors/collection'

import type { ActivateWarrantyWearerInput } from '@/graphql/api'
import {
    useActivateWarrantyMutation,
    useWarrantyActivationQuery,
} from './ComponentWarrantyActivation.generated'

import { useFrontendError } from '@/composables/useFrontendError'
import { useEmailError } from '@/composables/useEmailError'
import {
    createResponseAndPath,
    useLastResponseErrorAtPath,
    useResponseError,
} from '@/helpers/errors'

import Radio, { type RadioOption } from '@/components/ui/inputs/Radio.vue'
import TextField from '@/components/ui/inputs/TextField.vue'
import ThCheckbox from '@/components/ui/inputs/ThCheckbox.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import FormActionErrors from '@/components/ui/form/error/FormActionErrors.vue'
import ErrorGeneral from '@/components/ui/error/ErrorGeneral.vue'

const slug = useRouteParams('slug')
// The slug can become null when navigating away and suspense still loading
const id = computed(() => slug.value?.[1] || '')

const warrantyActivated = useWarrantyActivationQuery({
    variables: computed(() => ({ id: id.value })),
})

const mutation = useActivateWarrantyMutation()

const { executeMutation, fetching: fetchingMutation } = mutation

const errorsMutation = useLastResponseErrorAtPath([
    createResponseAndPath(mutation, 'activateWarranty'),
])

const wearerOptions: RadioOption[] = [
    { id: 'yes', label: 'Yes', value: 'Yes' },
    { id: 'no', label: 'No', value: 'No' },
]

const wearer = ref<'Yes' | 'No' | null>(null)

const name = ref('')
const email = ref('')
const emailConsent = ref(false)

const nameEmptyError: BackendError = {
    message: 'This field is required',
    path: ['input', 'wearer', 'name'],
    isInternalServerError: false,
    isUnauthorized: false,
}

const { error: namesError, validate: nameValidate } = useFrontendError(
    name,
    (value) => {
        if (!value) {
            return {
                valid: false,
                error: nameEmptyError,
            }
        }

        return {
            valid: true,
            value,
        }
    }
)

const { error: emailError, validate: emailValidate } = useEmailError(email, [
    'input',
    'email',
])

const isSubmitDisabled = computed(
    () =>
        fetchingMutation.value ||
        !wearer.value ||
        (wearer.value === 'No' &&
            (!name.value || Boolean(emailError.value?.message)))
)

const success = ref(false)

const submitWarranty = async () => {
    const email = emailValidate()
    const name = nameValidate()

    const wearerInput: ActivateWarrantyWearerInput | null =
        wearer.value === 'Yes' || !email || !name
            ? null
            : {
                  name,
                  email,
                  emailConsent: emailConsent.value,
              }

    const { error } = await executeMutation({
        input: {
            id: id.value,
            wearer: wearerInput,
        },
    })

    if (!error?.graphQLErrors.length) {
        success.value = true
    }
}

await warrantyActivated

// Prevent sending further reqeusts
// Otherwise it will send a request when navigating away (in which case id becomes
// an empty string)
warrantyActivated.pause()

const errorsQuery = useResponseError(warrantyActivated)

const { data, error: errorsWarantyActivated } = warrantyActivated

const alreadyActivated = toRef(() => data.value?.warrantyActivated)
</script>

<style lang="scss" scoped>
.component-warranty-activation {
    .warranty-activated-message {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        background-color: $stone-2;
        border: 1px solid $grey-200;
        border-radius: 12px;

        font-size: 1.125rem;
        font-weight: 500;
        text-align: center;
        gap: 1rem;

        &::before {
            content: '✓';
            font-size: 1.5rem;
            color: #4caf50;
            font-weight: bold;
        }
    }

    .form {
        max-width: 680px;
        padding: 40px 20px;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 0 auto;
        background-color: $stone-2;

        @include min-width(768) {
            padding: 40px;
        }

        .radio {
            width: 100%;
            display: flex;
            flex-wrap: wrap;

            .radio-legend {
                width: 100%;
                font-size: 1.125rem;
                font-weight: 500;
                margin-bottom: 2rem;
            }

            :deep(.choice) {
                margin-right: 30px;
            }
        }

        .fields {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 2rem;
            margin-bottom: 2rem;
        }
    }
}
</style>
